import React, { forwardRef, useEffect, useState, useMemo, useCallback } from 'react';
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import _ from 'lodash';
import { FieldTypes, parseSelectOptions } from 'utils/formUtils';
import message from "components/CustomMessage";
import GenericMappingForm from '../GenericMappingForm';
import { Form } from 'antd';

const MendIOMappingForm = forwardRef(function MendIOMappingForm({ disableProjectFields = false, ...props }, ref) {
  const [aplications, setAplications] = useState([]);
  const [aplicationsList, setAplicationList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [initialValues] = useState(() => {
    if (props.record) {
      return undefined;
    }
    return [
      {
        product: props.defaultProduct,
        subProduct: props.defaultSubProduct,
        subProductId: props.defaultSubProductId,
        environment: props.defaultEnvironment,
      },
    ];
  });

  const parseOrganisationsListing = (projectsListing) => {

    let aplicationMapping = {};
    Object.keys(projectsListing).forEach(_project => {
      projectsListing[_project].forEach(project => {
        const { otherProperties: { applicationId, applicationName } = {} } = project;
        aplicationMapping = { ...aplicationMapping, [applicationId]: applicationName };
      })
    });
    setAplicationList(_.map(aplicationMapping, (name, id) => ({ id, name })));
  };


  useEffect(() => {
    setLoading(true);
    const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, { login_id: props.loginConfigId });
    promise.then((response) => {
      const { projects } = response;
      const aplication = _.groupBy(projects, 'otherProperties.applicationName');
      parseOrganisationsListing(aplication)
      setAplications(aplication);
    }).catch(() => {
      message.error('Problem fetching projects');
    }).finally(() => setLoading(false));

    return cancel;
  }, [props.loginConfigId, props.toolName]);


  const getExtraFieldsData = useCallback((config) => {
    const { projectId, applicationId } = config;

    let projects = [];

    _.forEach(applicationId, (applicationId) => {
      const applicationName = aplicationsList.find(aplication => aplication.id === applicationId)?.name;
      projects = [...projects, ...(aplications[applicationName] || [])];
    });

    const applicationName = _.map(applicationId, id => aplicationsList.find(aplication => aplication.id === id)?.name).join(',');

    const projectName = _.map(projectId, id => {
      const project = _.find(projects, (obj) => obj.id === id);
      const prependAplicationName = `${project.otherProperties.applicationName}/${project.name}`;
      return prependAplicationName;
    }).join(',');
    
    return ({
      projectId: projectId.join(','),
      projectName,
      applicationId: applicationId.join(','),
      applicationName,
      //send the property received in config
      ...(config.discovery_config_id && { discovery_config_id: config.discovery_config_id })
    });
  }, [aplications, aplicationsList]);


  const changeProject = useCallback((value, field) => {
    const { name } = field;
    const configurations = form.getFieldValue("configurations");
    let updatedConfiguration = [
      ...configurations,
    ];
    updatedConfiguration[name] = {
      ...updatedConfiguration[name],
      projectId: undefined,
    }
    form.setFieldsValue({ configurations: updatedConfiguration });
  }, [form]);


  const getFilteredProjectOptions = useCallback((allApplications) => {
    if (_.isEmpty(allApplications)) {
      return;
    }

    let filteredProjects = [];

    _.forEach(allApplications, (applicationId) => {
      const applicationName = aplicationsList.find(aplication => aplication.id === applicationId)?.name;
      filteredProjects = [...filteredProjects, ...(aplications[applicationName] || [])];
    });

    const projectOptions = filteredProjects.map(project => ({ label: project.name, value: project.id }));
    return parseSelectOptions(projectOptions);

  }, [aplications, aplicationsList]);

  const isProjectFieldDisabled = useCallback((fieldKey, form) => {
    const fieldConfiguration = form.getFieldValue("configurations")[fieldKey];
    return (!fieldConfiguration || !fieldConfiguration.applicationId);
  }, []);


  const extraFields = useMemo(() => ([
    {
      name: 'applicationId',
      colSpan: '12',
      type: FieldTypes.MULTI_SELECT,
      label: 'Application Name',
      options: parseSelectOptions(aplicationsList),
      disabled: disableProjectFields,
      rules: [{ required: true, message: `Application Name is required` }],
      props: {
        onChange: changeProject,
        autoFocus: true
      }
    },
    {
      name: 'projectId',
      colSpan: '12',
      type: FieldTypes.MULTI_SELECT,
      label: 'Project Name',
      rules: [{ required: true, message: `Project Name is required` }],
      propsGenerator: (fieldKey) => ({ disabled: isProjectFieldDisabled(fieldKey, form) || disableProjectFields }),
      props: {
        getFormListOptions: (fieldKey) => {
          const fieldConfiguration = form.getFieldValue("configurations")[fieldKey];
          return getFilteredProjectOptions(fieldConfiguration?.applicationId);
        },
      },
    }
  ]), [aplicationsList, disableProjectFields, changeProject, isProjectFieldDisabled, form, getFilteredProjectOptions]);


  return (
    <GenericMappingForm
      form={form}
      toolLoading={loading}
      ref={ref}
      getExtraFieldsData={getExtraFieldsData}
      extraFields={extraFields}
      formDefaultValues={initialValues}
      {...modifyProps(props)}
    />
  )
})

export default MendIOMappingForm;

const modifyProps = (props) => {
  if (!props.record) {
    return props;
  }

  return {
    ...props,
    record: {
      ...props.record,
      projectId: props.record.projectId?.split(',') || [],
      projectName: props.record.projectName?.split(',') || [],
      applicationId: props.record.applicationId?.split(',') || [],
      applicationName: props.record.applicationName?.split(',') || [],
    }
  }
};
